/**
 * useShellDomainPeople - who is at the table and how they move: the roster
 * bar's verbs, the weapon rack, target templates, the character library,
 * the waiting rooms, scenes, spawning, shared loadouts, the vox, handouts
 * and session files.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import useShellPinned from './useShellPinned';
import useKillTeamActions from './useKillTeamActions';
import useKillTeamReconcile from './useKillTeamReconcile';
import { resolveCohesionChallengeFailure, resolveCohesionDamage, resolveOneSquadAbilityPerTurn } from '../multiplayer';
import { infoLine } from './shellCombatant';
import { useCallback, useMemo } from 'react';
import useShellTestCalls from './useShellTestCalls';
import useAnswerTestAsk from './useAnswerTestAsk';
import { actedByFrom } from '../engine/roster/actedAs';
import useShellQuietMoves from './useShellQuietMoves';
import useShellApproach from './useShellApproach';
import useStealthSightings from './useStealthSightings';
import useShellTabs from './useShellTabs';
import useSlotManagement from './useSlotManagement.js';
import useShellLibrary, { useShellLoadouts } from './useShellLibrary';
import useProfileManagement from './useProfileManagement.js';
import useActorProfiles from './useActorProfiles.js';
import useShellWaitingRooms from './useShellWaitingRooms';
import useShellScenes from './useShellScenes';
import useShellSpawn from './useShellSpawn';
import useShellImports from './useShellImports';
import useShellVox from './useShellVox';
import useShellHandouts from './useShellHandouts';
import useImportExport from './useImportExport.js';

/**
 * The roster bar's verbs and the weapon rack. Length: the hooks' argument
 * and return records (declarative).
 */
function useShellTabsAndRack(s) {
  const {
    selectedSlot,
    setSelectedSlot,
    setEditingSlot,
    slotClipboard,
    setSlotClipboard,
    setTabs,
    activeTab,
    setActiveTab,
    pointAtIfUnset,
    setRenamingTab,
    setTabCtx,
    sessionId,
    localCurrentProfile,
    setAtkProfiles,
    currentSlots,
    removeToken,
    sync,
    syncTargetToFirestore,
    removeCombatant,
    isLocked,
    isDefencePending,
    tabs,
    updateTab,
    turnTracker,
    sheetForCombatant,
  } = s;
  // The roster bar's verbs, keeping the turn order and the map in step.
  const tabVerbs = useShellTabs({
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    setTabCtx,
    setRenamingTab,
    isLocked,
    syncTargetToFirestore,
    updateTab,
    sessionId,
    sync,
    isDefencePending,
    pointAtIfUnset,
    turnTracker,
    sheetForCombatant,
    removeToken,
    removeCombatant,
    // The horde tools' lines, in the story lane (plan map-display, B), and who they refuse.
    // A line may carry a beat (plan design-gaps, V4: a horde's split or break), so every screen plays it on the map.
    narrate: useCallback(
      (text, tabId, beat) =>
        s.addNarrativeRef?.current?.(tabId, { ...infoLine(text), lane: 'story', ...(beat ? { beat } : {}) }),
      [s.addNarrativeRef],
    ),
    isHidden: s.hidden?.isHidden,
  });

  const {
    handleDuplicateSlot,
    handleCutSlot,
    handleCopySlot,
    handlePasteSlot,
    handleDeleteSlot,
    handleMoveSlot,
    handleMoveSlotBy,
    handleSaveSlot,
  } = useSlotManagement({
    currentSlots,
    localCurrentProfile,
    selectedSlot,
    setSelectedSlot,
    slotClipboard,
    setSlotClipboard,
    setAtkProfiles,
    setEditingSlot,
    isLocked,
  });
  return {
    ...tabVerbs,
    handleDuplicateSlot,
    handleCutSlot,
    handleCopySlot,
    handlePasteSlot,
    handleDeleteSlot,
    handleMoveSlot,
    handleMoveSlotBy,
    handleSaveSlot,
  };
}

/**
 * Target templates, the character library and the attacker's profile menu.
 * Length: the hooks' argument records (declarative).
 */
function useShellLibraries(s) {
  const {
    setSelectedSlot,
    setShowAtkMenu,
    setNewAtkName,
    tgtTemplates,
    setTgtTemplates,
    setShowTgtMenu,
    setNewTgtName,
    roster,
    setRoster,
    actorId,
    setActorId,
    actor,
    setTabs,
    activeTab,
    setActiveTab,
    sessionId,
    showDialogAsync,
    sync,
    syncTargetToFirestore,
    isLocked,
    currentTab,
    addNarrative,
  } = s;

  const { handleSaveTgtTemplate, handleDeleteTgtTemplate, handleLoadTgtTemplate } = useProfileManagement({
    tgtTemplates,
    setTgtTemplates,
    currentTab,
    setTabs,
    setActiveTab,
    setShowTgtMenu,
    setNewTgtName,
    isLocked,
    showDialogAsync,
    syncTargetToFirestore,
  });

  // The character library (the session's, or this client's own) and
  // saving somebody back into it. See `hooks/useShellLibrary`.
  const { activeLibrary, handleSaveCombatantToLibrary, handleSaveStanding } = useShellLibrary({
    sessionId,
    sync,
    tableAssets: s.tableAssets,
    roster,
    activeTab,
    showDialogAsync,
    addNarrative,
  });

  // Attacker "profile" menu → roster operations (load = act as, new = add,
  // save-as = duplicate, delete = remove). See hooks/useActorProfiles.
  const { handleLoadAtkProfile, handleNewAtkProfile, handleSaveAtkProfile, handleDeleteAtkProfile } = useActorProfiles({
    roster,
    setRoster,
    actorId,
    setActorId,
    actor,
    setSelectedSlot,
    setShowAtkMenu,
    setNewAtkName,
    isLocked,
    showDialogAsync,
  });

  return {
    handleSaveTgtTemplate,
    handleDeleteTgtTemplate,
    handleLoadTgtTemplate,
    activeLibrary,
    handleSaveCombatantToLibrary,
    handleSaveStanding,
    handleLoadAtkProfile,
    handleNewAtkProfile,
    handleSaveAtkProfile,
    handleDeleteAtkProfile,
  };
}

/**
 * The waiting rooms and the deploy bar's REPLACE. Length: the hook's argument
 * and return records (declarative).
 */
function useShellRooms(s) {
  const {
    roster,
    setRoster,
    actorId,
    setActorId,
    activeTab,
    setActiveTab,
    pointAtIfUnset,
    sessionId,
    addNarrativeRef,
    tokens,
    writeToken,
    removeToken,
    pushToast,
    removeCombatant,
    permissions,
    isLocked,
    isDefencePending,
    turnTracker,
    activeLibrary,
  } = s;
  // The waiting rooms, the one door for admitting characters, the room
  // verbs and the deploy bar's REPLACE. See `hooks/useShellWaitingRooms`.
  const {
    canSeeRooms,
    waiting,
    narrateRoom,
    record,
    admit,
    admitAndRecord,
    activeRoom,
    setActiveRoomId,
    newRoom,
    sendToRoom,
    dismissFromRoom,
    moveToRoom,
    deleteRoom,
    deployRoom,
    deployMember,
    recallToRoom,
    recallManyToRoom,
    setReplaceDialog,
    replaceOutgoing,
    replaceEarlyRefusal,
    replaceFromDialog,
    replaceFromDrop,
  } = useShellWaitingRooms({
    sessionId,
    isLocked,
    activeTab,
    actorId,
    setActiveTab,
    setActorId,
    roster,
    setRoster,
    tokens,
    writeToken,
    removeToken,
    removeCombatant,
    pointAtIfUnset,
    isDefencePending,
    turnTracker,
    permissions,
    activeLibrary,
    addNarrativeRef,
    pushToast,
    hidden: s.hidden,
  });
  return {
    canSeeRooms,
    waiting,
    narrateRoom,
    record,
    admit,
    admitAndRecord,
    activeRoom,
    setActiveRoomId,
    newRoom,
    sendToRoom,
    dismissFromRoom,
    moveToRoom,
    deleteRoom,
    deployRoom,
    deployMember,
    recallToRoom,
    recallManyToRoom,
    setReplaceDialog,
    replaceOutgoing,
    replaceEarlyRefusal,
    replaceFromDialog,
    replaceFromDrop,
  };
}

/**
 * Scenes and the spawn dialog. Length: the hooks' argument records
 * (declarative).
 */
function useShellStage(s) {
  const {
    setSelectedSlot,
    setFireMode,
    roster,
    setRoster,
    setActorId,
    setTabs,
    activeTab,
    setActiveTab,
    pointAtIfUnset,
    isGm,
    sessionId,
    addNarrativeRef,
    showDialogAsync,
    terrain,
    setTerrain,
    tokens,
    writeToken,
    removeToken,
    deployment,
    setDeployment,
    arrivals,
    setArrivals,
    syncTargetToFirestore,
    removeCombatant,
    setTabSourceCharacter,
    permissions,
    isLocked,
    pendingDefences,
    avoidOnDeploy,
    resetHistory,
    addNarrative,
    turnTracker,
    narrateRoom,
    record,
    admit,
    admitAndRecord,
    direction,
  } = s;

  // ---- Scenes: the stage and the wings (v8.337.0) ----
  const { stageSync, scenes } = useShellScenes({
    sessionId,
    isGm,
    roster,
    setRoster,
    tokens,
    writeToken,
    removeToken,
    removeCombatant,
    terrain,
    setTerrain,
    deployment,
    setDeployment,
    arrivals,
    setArrivals,
    turnTracker,
    permissions,
    avoidOnDeploy,
    pendingDefences,
    narrateRoom,
    resetHistory,
    // The table domain's wrapped one: a cut into another phase clears the scene's promises too.
    setPhase: direction.setPhase,
    hidden: s.hidden,
  });

  // Characters from the library onto the roster: the spawn dialog, its
  // identity question, and the bulk load. See `hooks/useShellSpawn`.
  const { spawnDialog, setSpawnDialog, handleLoadCharacter, loadCharacters, handleSpawnConfirm } = useShellSpawn({
    roster,
    setRoster,
    setTabs,
    activeTab,
    setActiveTab,
    setActorId,
    setSelectedSlot,
    setFireMode,
    setTabSourceCharacter,
    syncTargetToFirestore,
    addNarrative,
    addNarrativeRef,
    turnTracker,
    isLocked,
    showDialogAsync,
    record,
    admitAndRecord,
    admit,
    pointAtIfUnset,
    // Born hidden (plan map-display, G1): the GM's reservation in the hidden set, made before the roster add.
    reserveHidden: s.hidden?.reserve,
  });
  return { stageSync, scenes, spawnDialog, setSpawnDialog, handleLoadCharacter, loadCharacters, handleSpawnConfirm };
}

/**
 * Shared loadouts, what a session file's import does, and the vox. Length:
 * the hooks' argument records (declarative).
 */
function useShellLoadoutsAndVox(s) {
  const {
    user,
    connectedPlayers,
    roster,
    setRoster,
    setActorId,
    actor,
    updateCombatantState,
    updateCombatantSheet,
    setActiveTab,
    isGm,
    sessionId,
    playerId,
    chatToasts,
    summonLog,
    writeToken,
    importMaps,
    pushToast,
    isLocked,
    loadMapWithRules,
    addRollNarrative,
    turnTracker,
    asks,
    unnaturalDosMode,
  } = s;

  // ---- Shared loadout library (D4): templates stamped onto the actor ----
  const { loadouts, saveLoadout, deleteLoadout, handleSaveLoadout, handleApplyLoadout, handleSaveLoadoutToLibrary } =
    useShellLoadouts({
      sessionId,
      playerId,
      actor,
      updateCombatantSheet,
      updateCombatantState,
      armouryShelves: s.armouryShelves,
    });

  // What a session file's import does to the live table.
  const { handleImportRoster, handleImportLoadouts, handleImportBattlefield, handleImportCharacters } = useShellImports(
    {
      setRoster,
      setActiveTab,
      setActorId,
      turnTracker,
      saveLoadout,
      loadMapWithRules,
      importMaps,
      writeToken,
    },
  );

  // ---- The vox (v8.363.4): seats, who they act as, and the chat ----
  const { actingBy, voxPeople, voxMe, channelsApi, chatContextValue } = useShellVox({
    connectedPlayers,
    roster,
    sessionId,
    user,
    playerId,
    isGm,
    actor,
    addRollNarrative,
    isLocked,
    chatToasts,
    summonLog,
    pushToast,
  });

  // Called tests: the GM asks everyone who has acted as the person (actedBy); the asked screen rolls
  // and answers with the result, this screen credits it - see the hooks.
  const actedBy = useMemo(() => actedByFrom(connectedPlayers ?? []), [connectedPlayers]);
  const { testCalls, postSecret, dispositions } = useShellTestCalls({
    assistBonus: s.assistBonus,
    story: s.story,
    roster,
    asks,
    actingBy,
    actedBy,
    voxPeople,
    voxMe,
    sessionId,
    isGm,
    addRollNarrative,
    unnaturalDosMode,
  });
  const answerTest = useAnswerTestAsk({ roster, asks, unnaturalDosMode, assistBonus: s.assistBonus });

  // Stealth's quiet work: a plate becomes a request, the GM's screen rolls it and proposes the ladder
  // step. Fills `quietMoveRef`, which the drum and the trigger (an earlier domain) fire through.
  const quiet = useShellQuietMoves({ ...s, postSecret });
  // The group approach: roles on the ladder, how well it went on the GM's desk - see the hook.
  const approach = useShellApproach({ ...s, postSecret, quiet });
  // When the opposition could see someone: the GM's question (Roll it / Seen / Missed) - see the hook.
  const { sightings } = useStealthSightings({ ...s, testCalls });

  return {
    testCalls,
    sightings,
    answerTest,
    actedBy,
    dispositions,
    quiet,
    approach,
    loadouts,
    deleteLoadout,
    handleSaveLoadout,
    handleApplyLoadout,
    handleSaveLoadoutToLibrary,
    handleImportRoster,
    handleImportLoadouts,
    handleImportBattlefield,
    handleImportCharacters,
    actingBy,
    voxPeople,
    voxMe,
    channelsApi,
    chatContextValue,
  };
}

/**
 * Handouts and the session file's export and import. Length: the hooks'
 * argument and return records (declarative).
 */
function useShellFiles(s) {
  const {
    tgtTemplates,
    setTgtTemplates,
    roster,
    setTabs,
    activeTab,
    setActiveTab,
    setPendingAttack,
    isGm,
    sessionId,
    atkProfiles,
    importAtkProfiles,
    terrain,
    tokens,
    library,
    syncAllTabsToFirestore,
    clearAllPendingDefences,
    tabs,
    addNarrative,
    waiting,
    loadouts,
    handleImportRoster,
    handleImportLoadouts,
    handleImportBattlefield,
    handleImportCharacters,
    voxPeople,
    voxMe,
    channelsApi,
  } = s;
  // ---- Handouts (v8.363.10): the drawer, the lightbox and the reveal ----
  // Everything but the import is handed on as it is.
  const { handleImportHandouts, ...handoutParts } = useShellHandouts({
    sessionId,
    isGm,
    voxMe,
    voxPeople,
    channels: channelsApi.channels,
    tableAssets: s.tableAssets,
  });

  const {
    doExport,
    doImport,
    showExportDialog,
    handleExportConfirm,
    handleExportCancel,
    importDialogData,
    handleImportConfirm,
    handleImportCancel,
  } = useImportExport({
    tabs,
    setTabs,
    activeTab,
    setActiveTab,
    atkProfiles,
    importAtkProfiles,
    tgtTemplates,
    setTgtTemplates,
    syncAllTabsToFirestore,
    addNarrative,
    setPendingAttack,
    clearAllPendingDefences,
    roster,
    importRoster: handleImportRoster,
    loadouts,
    importLoadouts: handleImportLoadouts,
    terrain,
    mapLibrary: library,
    tokens,
    waitingRooms: waiting.rooms,
    importBattlefield: handleImportBattlefield,
    importCharacters: handleImportCharacters,
    handouts: handoutParts.handoutsApi.handouts,
    importHandouts: handleImportHandouts,
  });
  return {
    ...handoutParts,
    doExport,
    doImport,
    showExportDialog,
    handleExportConfirm,
    handleExportCancel,
    importDialogData,
    handleImportConfirm,
    handleImportCancel,
  };
}

/**
 * @param {object} s - the shell scope (everything the attack domain and those
 *   before it declared).
 * @returns {object} the tab, slot, template, library and profile verbs, the
 *   rooms and scenes, the spawn dialog, loadouts, the vox, handouts and the
 *   export / import dialogs.
 */
/**
 * The kill-team's verbs (Squad Mode, see the hook): over the SHARED roster - a hidden figure is never a brother who
 * counts - and the rule tokens, never the raw markers (the positions seam). Length: one argument record (declarative).
 */
function useShellKillTeam(s) {
  const { ruleTokens, permissions } = s;
  const killTeamActions = useKillTeamActions({
    roster: s.sharedRoster ?? s.roster,
    tokens: ruleTokens,
    sceneId: s.killTeams?.sceneId ?? null,
    killTeams: s.killTeams ?? null,
    // A table with no session is its GM's (as undo's frame rule reads it).
    isGm: !!s.isGm || !s.sessionId,
    challengeFailure: resolveCohesionChallengeFailure(permissions),
    oneSquadAbilityPerTurn: resolveOneSquadAbilityPerTurn(permissions),
    cohesionDamage: resolveCohesionDamage(permissions),
    updateCombatantState: s.updateCombatantState,
    addRollNarrative: s.addRollNarrative,
    capturePreActionSnapshot: s.capturePreActionSnapshot,
    pushHistory: s.pushHistory,
    pushToast: s.pushToast,
    historyTrack: s.historyTrack,
  });
  useKillTeamReconcile({
    isGm: !!s.isGm || !s.sessionId,
    roster: s.sharedRoster ?? s.roster,
    killTeams: s.killTeams ?? null,
    actions: killTeamActions,
  });
  // The domains before this one reach the verbs through the ref (see useShellDomainSync).
  if (s.killTeamVerbs) s.killTeamVerbs.current = killTeamActions;
  return { killTeamActions };
}

export default function useShellDomainPeople(s) {
  const tabsAndRack = useShellTabsAndRack(s);
  const libraries = useShellLibraries({ ...s, ...tabsAndRack });
  const rooms = useShellRooms({ ...s, ...tabsAndRack, ...libraries });
  const stage = useShellStage({ ...s, ...tabsAndRack, ...libraries, ...rooms });
  const loadoutsAndVox = useShellLoadoutsAndVox({ ...s, ...tabsAndRack, ...libraries, ...rooms, ...stage });
  const files = useShellFiles({ ...s, ...tabsAndRack, ...libraries, ...rooms, ...stage, ...loadoutsAndVox });
  const killTeam = useShellKillTeam({ ...s, ...stage });
  // The Pinned question (2026-10-25): it needs who acts as whom, which the vox has just learnt.
  const pinned = useShellPinned({ ...s, ...loadoutsAndVox });
  return { ...tabsAndRack, ...libraries, ...rooms, ...stage, ...loadoutsAndVox, ...files, ...killTeam, pinned };
}
